package fr.arcplex.sooner.webui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import fr.arcplex.sooner.coherence.CoherenceThemeState
import fr.arcplex.sooner.infrastructure.BootState
import fr.arcplex.network.remoteImageUrl
import fr.arcplex.sooner.ui.theme.TVBackground
import fr.arcplex.sooner.ui.theme.TVMuted
import fr.arcplex.sooner.ui.theme.TVPlay
import fr.arcplex.sooner.ui.theme.TVTxt

/**
 * Skin of the boot screen. The legacy view uses the defaults below, the
 * Coherence view derives the same fields from CoherenceThemeState.
 */
data class SplashStyle(
    val background: Color = TVBackground,
    val textColor: Color = TVTxt,
    val mutedColor: Color = TVMuted,
    val accent: Color = TVPlay,
    val title: String = "Sooner",
    val loadingLabel: String = "Chargement…",
    val errorLabel: String = "Impossible de charger l'application",
    val retryLabel: String = "Réessayer",
    val titleSize: TextUnit = 34.sp,
    val labelSize: TextUnit = 16.sp
)

/**
 * Boot screen of the legacy path: brand background (the page one when known)
 * plus a centred loader, replaced by the error and retry action on failure.
 */
@Composable
fun SplashView(
    state: BootState,
    error: String?,
    page: Page?,
    onRetry: () -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    SplashPanel(
        state = state,
        error = error,
        backgroundImage = page?.bgImage,
        style = SplashStyle(
            background = page?.bgColor?.let { parseColor(it) } ?: TVBackground
        ),
        onRetry = onRetry,
        modifier = modifier
    )
}

/**
 * Same boot screen with colors and text sizes from the Coherence theme; the
 * labels payload has no boot entries yet, so the French defaults are kept
 * unless a matching key is served.
 */
@Composable
fun CoherenceSplashView(
    state: BootState,
    error: String?,
    page: Page?,
    onRetry: () -> Unit,
    isTV: Boolean,
    modifier: Modifier = Modifier
) {
    SplashPanel(
        state = state,
        error = error,
        backgroundImage = page?.bgImage,
        style = coherenceSplashStyle(),
        onRetry = onRetry,
        modifier = modifier
    )
}

@Composable
private fun coherenceSplashStyle(): SplashStyle {
    val theme = CoherenceThemeState.theme
    val page = theme.page
    return SplashStyle(
        background = parseHexColorOrNull(page.background) ?: TVBackground,
        textColor = parseHexColorOrNull(page.text) ?: TVTxt,
        mutedColor = parseHexColorOrNull(page.muted) ?: TVMuted,
        accent = parseHexColorOrNull(page.accent) ?: TVPlay,
        titleSize = (theme.kinds.rail.titleSize ?: 34).sp,
        labelSize = (theme.kinds.text.size ?: 16).sp
    )
}

@Composable
private fun SplashPanel(
    state: BootState,
    error: String?,
    backgroundImage: String?,
    style: SplashStyle,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier
) {
    val showError = state == BootState.ERROR || error != null

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(style.background),
        contentAlignment = Alignment.Center
    ) {
        if (!backgroundImage.isNullOrBlank()) {
            AsyncImage(
                model = remoteImageUrl(backgroundImage),
                contentDescription = null,
                contentScale = ContentScale.Crop,
                modifier = Modifier.fillMaxSize()
            )
            // Keep the loader readable over the artwork.
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(style.background.copy(alpha = 0.75f))
            )
        }

        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier.fillMaxSize().padding(32.dp)
        ) {
            Text(
                text = style.title,
                color = style.textColor,
                fontSize = style.titleSize,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(32.dp))

            if (showError) {
                Text(
                    text = style.errorLabel,
                    color = style.textColor,
                    fontSize = style.labelSize,
                    textAlign = TextAlign.Center
                )
                if (!error.isNullOrBlank()) {
                    Text(
                        text = error,
                        color = style.mutedColor,
                        fontSize = style.labelSize,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(top = 8.dp)
                    )
                }
                Spacer(modifier = Modifier.height(24.dp))
                Button(
                    onClick = onRetry,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = style.accent,
                        contentColor = style.background
                    )
                ) {
                    Text(text = style.retryLabel)
                }
            } else {
                CircularProgressIndicator(color = style.accent)
                Spacer(modifier = Modifier.height(16.dp))
                Text(
                    text = style.loadingLabel,
                    color = style.mutedColor,
                    fontSize = style.labelSize,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}
